/* eslint-disable react/jsx-pascal-case */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { MyFirebase } from '../../api/my_firebase.ts';
import * as StripePayment from '../../api/stripe_paypment.ts';
import Dashboard from '../dashboard/Dashboard.tsx';
import CancelFlowController from './cancellation_flow/CancelFlowController.js';
import { useFreshchat } from '../../freshchat/useFreshchat.ts';

import {
  Button,
  Typography,
  Box,
  Input,
  Divider,
  Stack,
  Skeleton
} from '@mui/joy';

const Settings = () => {
  const { isPremium, email } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);
  const { handleSignOut } = useFreshchat();

  const app = MyFirebase.getInstance().getApp();

  // Check if all data is loaded
  useEffect(() => {
    if (email !== undefined && isPremium !== undefined) {
      // Add a small delay to ensure all child components are also ready
      const timer = setTimeout(() => {
        setIsDataReady(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [email, isPremium]);

  const handleLogout = () => {
    // Reset Freshchat user state before signing out
    handleSignOut();
    // Then sign out from Firebase
    MyFirebase.getInstance().signOut();
  };

  // Skeleton loader component
  const SettingsSkeleton = () => (
    <Box sx={{ maxWidth: '600px', p: 3 }}>
      <Skeleton variant="text" level="h1" sx={{ fontSize: "2rem", mb: 4, width: '200px' }} />
      
      <Stack spacing={4} alignItems="flex-start">
        {/* Email Section Skeleton */}
        <Box sx={{ width: '100%' }}>
          <Skeleton variant="text" level="h3" sx={{ fontSize: '1.125rem', mb: 2, width: '150px' }} />
          <Skeleton variant="rectangular" sx={{ width: '100%', maxWidth: '400px', height: '56px', borderRadius: '8px' }} />
        </Box>

        <Divider sx={{ width: '100%' }} />

        {/* Billing Section Skeleton */}
        <Box sx={{ width: '100%' }}>
          <Skeleton variant="text" level="h3" sx={{ fontSize: '1.125rem', mb: 2, width: '180px' }} />
          <Skeleton variant="rectangular" sx={{ width: '200px', height: '48px', borderRadius: '8px' }} />
        </Box>

        {/* Cancellation Section Skeleton */}
        <Box sx={{ width: '100%' }}>
          <Skeleton variant="text" level="h3" sx={{ fontSize: '1.125rem', mb: 2, width: '200px' }} />
          <Skeleton variant="rectangular" sx={{ width: '180px', height: '48px', borderRadius: '8px' }} />
        </Box>

        <Divider sx={{ width: '100%' }} />

        {/* Logout Section Skeleton */}
        <Box sx={{ width: '100%' }}>
          <Skeleton variant="text" level="h3" sx={{ fontSize: '1.125rem', mb: 2, width: '140px' }} />
          <Skeleton variant="rectangular" sx={{ width: '120px', height: '48px', borderRadius: '8px' }} />
        </Box>
      </Stack>
    </Box>
  );

  // Show skeleton while loading
  if (!isDataReady) {
    return (
      <Dashboard>
        <Helmet>
          <title>Settings</title>
          <meta name="description" content={"Account Settings"} />
        </Helmet>
        <SettingsSkeleton />
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      <Helmet >
        <title>Settings</title>
        <meta name="description" content={"Account Settings"} />
      </Helmet>
      <Box>
        <Typography
          level="h1"
          sx={{
            fontSize: "1.8em",
            fontWeight: 'lg',
            fontFamily: "Libre Baskerville",
            pb: 2
          }}
        >
          Account Settings
        </Typography>

        <Stack spacing={4} alignItems="flex-start">
          {/* Email Section */}
          <Box sx={{ width: '100%' }}>
            <Typography
              level="h3"
              sx={{
                fontSize: '1.125rem',
                fontWeight: 600,
                mb: 2,
                color: 'text.primary'
              }}
            >
              Email
            </Typography>
            <Input
              size="lg"
              placeholder="Email"
              variant="outlined"
              disabled
              value={email || ''}
              sx={{
                width: '100%',
                maxWidth: '400px',
                '& .MuiInput-root': {
                  borderRadius: '8px',
                  borderColor: 'divider',
                  bgcolor: 'background.level1',
                  '&:hover': {
                    borderColor: 'primary.300'
                  }
                }
              }}
            />
          </Box>

          <Divider sx={{ width: '100%' }} />

          {/* Billing Section */}
          {isPremium && (
            <Box sx={{ width: '100%' }}>
              <Typography
                level="h3"
                sx={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  mb: 2,
                  color: 'text.primary'
                }}
              >
                Billing
              </Typography>
              <Button
                variant="outlined"
                color="neutral"
                loading={loading}
                size="md"
                sx={{
                  bgcolor: 'background.level1',
                }}
                onClick={async () => {
                  setLoading(true);
                  const url = await StripePayment.getPortalUrl(app)
                  window.open(url, '_blank');
                  setLoading(false);
                }}
              >
                Manage Billing & Payments
              </Button>
            </Box>
          )}

          {/* Cancellation Section */}
          {false && isPremium && (
            <Box sx={{ width: '100%' }}>
              <Typography
                level="h3"
                sx={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  mb: 2,
                  color: 'text.primary'
                }}
              >
                Subscription Management
              </Typography>
              <CancelFlowController />
            </Box>
          )}

          {/* Logout Section */}
          <Box sx={{ width: '100%' }}>
            <Typography
              level="h3"
              sx={{
                fontSize: '1.125rem',
                fontWeight: 600,
                mb: 2,
                color: 'text.primary'
              }}
            >
              Account Actions
            </Typography>
            <Button
              variant="outlined"
              color="neutral"
              sx={{
                bgcolor: 'background.level1',
                textTransform: 'none',
              }}
              onClick={handleLogout}
            >
              Sign Out
            </Button>
          </Box>
        </Stack>
      </Box>
    </Dashboard>
  );
};

export default Settings;
